import React, { useEffect, useState } from "react";

import { DownMinimalArrow, Plus } from "icons";
import Pluralize from "pluralize";
import { isEmpty } from "ramda";

import fhirApi from "apis/fhir";
import {
  Checkbox,
  Dropdown,
  DropdownOverlay,
  PageLoader,
  SearchBar,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import Filter from "components/Common/FilterHeader/Filter";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { useUserState } from "contexts/user";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import { getPermissions } from "utils/roleBasedAccess";

import ConversionTable from "./ConversionTable";
import DetailsModal from "./DetailsModal";
import NewFHIRConversion from "./NewFHIRConversion";

import {
  FILTER_CONDITIONS,
  FILTER_OPERATIONS,
} from "../../../constants/stewardFilter";
import { COLUMNS, CONVERSIONS, FHIR_CONVERTER_COLUMN } from "../constants";

const FHIRConverter = ({ features }) => {
  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);

  const [isNewFHIRConversionOpen, setIsNewFHIRConversionOpen] = useState(false);
  const [filterConditions, setFilterConditions] = useState([]);
  const [search, setSearch] = useState("");
  const [fhirConversions, setFhirConversions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const fetchFhirConversions = async () => {
    try {
      const response = await fhirApi.fetch();
      setFhirConversions(response.data.fhir_conversions);
    } catch (e) {
      logger.error(e);
    }
  };

  const onModalClose = () => {
    setShowModal(false);
    setSelectedRow({});
  };

  useEffect(() => {
    fetchFhirConversions();
  }, []);

  const { columns, toggleVisibility } = useColumnHeaderSelector(
    FHIR_CONVERTER_COLUMN
  );

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8 mb-2"
        breadcrumbs={[
          { label: "Reference Data", link: "#" },
          { label: "FHIR Converter", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Dropdown
              label="Create FHIR Conversion"
              icon={Plus}
              primaryAction={() => {
                setIsNewFHIRConversionOpen(true);
              }}
            />
          </PermissibleWrapper>
        }
      />
      <div className="flex justify-between mt-8">
        <div className="flex items-center space-x-4">
          <Typography style="large" type="bold">
            {Pluralize("Conversions", CONVERSIONS.length, true)}
          </Typography>
          <Filter
            title="Conversions"
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            columnOptions={COLUMNS?.filter(column => column !== "Action").map(
              column => ({
                label: column,
                value: column,
              })
            )}
            conditionOptions={FILTER_CONDITIONS}
            filterOperations={FILTER_OPERATIONS}
          />
        </div>
        <div className="flex items-center space-x-4">
          <SearchBar
            placeholder="Search Conversions"
            className="w-72"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="flex gap-4">
            <DropdownOverlay
              buttonLabel="Edit Columns"
              buttonStyle="secondary"
              buttonProps={{ icon: DownMinimalArrow, iconPosition: "right" }}
            >
              <div className="w-48 p-4 pb-0">
                <Typography
                  style="h6"
                  className="mb-2 uppercase text-grey-medium-shade"
                >
                  enabled
                </Typography>
                {columns?.selected?.map(column => (
                  <Checkbox
                    key={column.key}
                    label={column.title}
                    id={column.key}
                    className="mb-5"
                    onClick={e =>
                      columns.selected.length > 1 &&
                      toggleVisibility(e.target.id)
                    }
                    checked
                    readOnly
                    disabled={columns.selected.length === 1}
                  />
                ))}
              </div>
              <div className="px-4">
                <Typography
                  style="h6"
                  className="mb-2 uppercase text-grey-medium-shade"
                >
                  disabled
                </Typography>
                {columns?.unSelected?.map(column => (
                  <Checkbox
                    key={column.key}
                    label={column.title}
                    id={column.key}
                    className="mb-5"
                    onClick={e => toggleVisibility(e.target.id)}
                    readOnly
                  />
                ))}
              </div>
            </DropdownOverlay>
          </div>
        </div>
      </div>
      {isEmpty(CONVERSIONS) ? (
        <PageLoader />
      ) : (
        <ConversionTable
          conversions={fhirConversions}
          selectedColumns={columns.selected}
          setShowModal={setShowModal}
          setSelectedRow={setSelectedRow}
        />
      )}
      <NewFHIRConversion
        isNewFHIRConversionOpen={isNewFHIRConversionOpen}
        setIsNewFHIRConversionOpen={setIsNewFHIRConversionOpen}
      />
      {showModal && (
        <DetailsModal
          showModal={showModal}
          selectedRow={selectedRow}
          onModalClose={onModalClose}
        />
      )}
    </div>
  );
};

export default FHIRConverter;
